import { EyeIcon, EyeOffIcon } from "lucide-react";
import { Chart as ChartJS, registerables } from "chart.js";
import { useInView } from "framer-motion";
import { memo, useMemo, useRef, useState, type CSSProperties } from "react";

import { ChartData, ChartFormatEnum } from "@/__generated__/graphql";
import { Icon } from "@/components/atoms/Icon";
import { BarChart } from "@/components/organisms/Chart/ChartBar";
import { LineChart } from "@/components/organisms/Chart/ChartLine";
import {
  ChartPie,
  PIE_SIZE_CONFIGS,
} from "@/components/organisms/Chart/ChartPie";
import { CHART_COLORS } from "@/constants/values";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";
import { Skeleton } from "@/components/ui/skeleton";

ChartJS.register(...registerables);

const COLORS = CHART_COLORS;
// const COLORS = [
//   "#3C5CD7",
//   "#5DD07F",
//   "#B19D4E",
//   "#E3F7E9",
//   "#2D9C4E",
//   "#52CC33",
//   "#9879E2",
//   "#0A2412",
// ];

export const BAR_COLOR_ORDER = [...COLORS];

export const PIE_COLOR_ORDER = [...COLORS];

export const LINE_COLOR_ORDER = [...COLORS];

/**
 * Whether a chart's bars are parts rather than steps.
 *
 * A bar per month is one thing measured again and again: the same colour each
 * time, named once. A bar per category is a different thing each time, and a
 * row of identical bars makes the reader carry the axis in their head.
 */
export function isBarsOfParts(
  type: "bar" | "line" | "pie",
  data: ChartData | null | undefined,
): boolean {
  return (
    type === "bar" &&
    (data?.series?.length ?? 0) <= 1 &&
    data?.labelFormat !== ChartFormatEnum.Date
  );
}

/**
 * The names a group goes by when there is nothing in it to name.
 *
 * A summary labels a null group "None" (``ChartShape.said``), but that is not
 * the only way the server says it: spending is coalesced onto "Uncategorized"
 * (``TransactionsService.spending_by_category``), and a bank transaction sits
 * on the "Uncoded" account until a journal entry books it. All three mean the
 * same thing - these rows have not been given one - so a chart treats them
 * alike: hatched rather than coloured, taking none of the palette, and put
 * away together by the eye in the legend.
 */
const EMPTY_GROUP_NAMES = new Set(["None", "Uncategorized", "Uncoded"]);

export function isEmptyGroup(
  label: string | number | null | undefined,
): boolean {
  return EMPTY_GROUP_NAMES.has(String(label));
}

/**
 * What an empty part is drawn as, rather than a colour of its own.
 *
 * Coloured like everything else, "None" reads as a category - one more thing
 * the money went to. Shaded between two greys instead, it stays part of the
 * whole, because it is a real share of the total, while saying there is
 * nothing in it to name. The lighter grey is a surface of the page itself and
 * the darker band over it is the palette's mid neutral at a fifth of its
 * strength - a grey halfway between the two schemes, so a fifth of it stands
 * off a near-white card as far as it does off a near-black one. The band is
 * twice the width of the gap it leaves.
 */
const EMPTY_SHADE =
  "color-mix(in srgb, var(--color-neutral-400) 20%, transparent)";
const EMPTY_SHADE_LIGHTER = "var(--muted)";
const EMPTY_BAND_LIGHTER = 1.5;
const EMPTY_BAND = EMPTY_BAND_LIGHTER * 2;

/** The shading in the plot itself, where the parts are SVG (a nivo pattern def). */
export const EMPTY_HATCH_DEF = {
  id: "chart-empty-hatch",
  type: "patternLines",
  background: EMPTY_SHADE_LIGHTER,
  color: EMPTY_SHADE,
  rotation: -45,
  lineWidth: EMPTY_BAND,
  spacing: EMPTY_BAND + EMPTY_BAND_LIGHTER,
} as const;

/** The same shading in CSS, for the swatches beside a chart's names. */
export const EMPTY_HATCH_CSS = `repeating-linear-gradient(-45deg, ${EMPTY_SHADE} 0 ${EMPTY_BAND}px, ${EMPTY_SHADE_LIGHTER} ${EMPTY_BAND}px ${EMPTY_BAND + EMPTY_BAND_LIGHTER}px)`;

/**
 * A line has no area to shade, so an empty one is drawn flat in a grey of its
 * own - the surfaces above are too close together to be seen as a stroke.
 */
export const EMPTY_COLOR = "var(--color-neutral-300)";

/** How many names a chart says out loud before it keeps them in a badge. */
const LEGEND_NAMES_INLINE = 3;

/**
 * What each colour in a chart stands for.
 *
 * A few names read as a caption and are simply shown. More than a few - a pie
 * of every category - read as noise: two lines of names above a chart, in
 * front of the chart, saying nothing until you go looking for one. Past that
 * the names go behind a badge that says how many there are and shows them
 * when they are asked for.
 */
function ChartLegend({
  labels,
  colors,
  loading,
  empty,
}: {
  labels: string[];
  colors: string[];
  loading: boolean;
  /** The group with nothing to name it, and how to put it away. */
  empty?: { isShown: boolean; onToggle: () => void; said: string };
}) {
  const names = (
    <div className="flex flex-col gap-1 pt-0.5 pb-0.5">
      {labels.map((label, index) => (
        <div key={label + index} className="flex items-center gap-2">
          <Swatch
            color={colors[index % colors.length]}
            label={label}
            loading={false}
          />
          <Typography level="body-sm" className="text-foreground">
            {label}
          </Typography>
        </div>
      ))}
    </div>
  );

  if (labels.length > LEGEND_NAMES_INLINE && !loading) {
    const badge = (
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            data-testid="chart-legend-badge"
            className="inline-flex items-center gap-1.5 pl-1.5 pr-1.5 pt-0.5 pb-0.5 rounded-sm cursor-default hover:bg-muted"
          >
            <div className="flex items-center gap-0.5">
              {labels.slice(0, LEGEND_NAMES_INLINE + 2).map((label, index) => (
                <Swatch
                  key={label + index}
                  color={colors[index % colors.length]}
                  label={label}
                  loading={false}
                />
              ))}
            </div>
            <Typography level="body-sm" className="text-muted-foreground/80">
              {labels.length} parts
            </Typography>
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" align="start">
          {names}
        </TooltipContent>
      </Tooltip>
    );
    return empty ? (
      <div className="flex items-center gap-1 min-w-0">
        {badge}
        <EmptyEye
          isShown={empty.isShown}
          onToggle={empty.onToggle}
          said={empty.said}
        />
      </div>
    ) : (
      badge
    );
  }

  return (
    <div className="flex flex-row flex-nowrap overflow-x-clip w-full items-center gap-2">
      {labels.map((label, index) => (
        <div key={label + index} className="flex items-center gap-2">
          <Swatch
            color={colors[index % colors.length]}
            label={label}
            loading={loading}
          />
          <Typography
            level="body-md"
            className={cn(
              "text-muted-foreground/80 whitespace-nowrap overflow-hidden text-ellipsis",
              empty &&
                isEmptyGroup(label) &&
                !empty.isShown &&
                "[text-decoration:line-through]",
            )}
          >
            <Skeleton loading={loading}>{label}</Skeleton>
          </Typography>
          {/* Beside the name it is about, rather than off at the other end of
              the header where it read as a setting of the whole chart. */}
          {empty && isEmptyGroup(label) && !loading && (
            <EmptyEye
              isShown={empty.isShown}
              onToggle={empty.onToggle}
              said={empty.said}
            />
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * Puts the empty group away, and brings it back.
 *
 * "Hide none" said neither what would go nor what would be left of the chart.
 * ``said`` is what the rows are and what they are cut by - "transactions with
 * no category" - so the button names the thing it acts on rather than the
 * label it sits beside.
 */
function EmptyEye({
  isShown,
  onToggle,
  said,
}: {
  isShown: boolean;
  onToggle: () => void;
  /** What is put away, in words: "transactions with no category". */
  said: string;
}) {
  const does = `${isShown ? "Hide" : "Show"} ${said}`;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={does}
          onClick={onToggle}
          className="inline-flex items-center [border:none] bg-none [background-position-x:initial] [background-position-y:initial] bg-size-[initial] [background-repeat:initial] [background-attachment:initial] [background-origin:initial] [background-clip:initial] bg-[initial] cursor-pointer p-0.5 rounded-sm text-muted-foreground/80 hover:bg-muted hover:text-muted-foreground"
        >
          <Icon ReactIcon={isShown ? EyeIcon : EyeOffIcon} size="xs" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom">{does}</TooltipContent>
    </Tooltip>
  );
}

function Swatch({
  color,
  label,
  loading,
}: {
  color: string;
  label?: string;
  loading: boolean;
}) {
  return (
    <div
      className={cn(
        "w-3 h-3 min-w-3 min-h-3",
        loading
          ? "bg-secondary"
          : isEmptyGroup(label)
            ? "bg-[repeating-linear-gradient(-45deg,color-mix(in_srgb,var(--color-neutral-400)_20%,transparent)_0,color-mix(in_srgb,var(--color-neutral-400)_20%,transparent)_3px,var(--muted)_3px,var(--muted)_4.5px)] [background-position-x:initial] [background-position-y:initial] bg-size-[initial] [background-repeat:initial] [background-attachment:initial] [background-origin:initial] [background-clip:initial] bg-[initial]"
            : "[background-image:initial] [background-position-x:initial] [background-position-y:initial] bg-size-[initial] [background-repeat:initial] [background-attachment:initial] [background-origin:initial] [background-clip:initial] bg-(--color)",
        "rounded-[4px]",
      )}
      style={
        {
          "--color": !loading && !isEmptyGroup(label) ? color : undefined,
        } as CSSProperties
      }
    />
  );
}

// Height mappings for different sizes
const SIZE_HEIGHTS = {
  xs: 200,
  sm: 300,
  md: 400,
  lg: 500,
};

function ChartComponent({
  data,
  type = "bar",
  height,
  loading = false,
  size = "md",
  lineKeys,
  hideTitle = false,
  fill = false,
  rows,
}: {
  data: ChartData;
  type: "bar" | "line" | "pie";
  height?: number;
  loading?: boolean;
  size?: "xs" | "sm" | "md" | "lg";
  lineKeys?: string[];
  /** What the chart counts, named: "transactions", "bank accounts". */
  rows?: string;
  /** When true, omit the chart title row (e.g. when the parent ReportsViewCard supplies the title). */
  hideTitle?: boolean;
  /** Grow to fill the parent flex region (dashboard widgets). */
  fill?: boolean;
}) {
  const chartHeight = height || SIZE_HEIGHTS[size];
  const headerBandHeight = hideTitle ? 32 : 72;
  const plotHeight = chartHeight - headerBandHeight;
  // A group the data has nothing to say about is still part of the whole, and
  // is often most of it: shown, it dwarfs everything named beside it; hidden,
  // the total is a lie. So it is drawn, and can be put away - offered only
  // where there is one to put away.
  const [showsEmpty, setShowsEmpty] = useState(true);
  const hasEmpty = (data?.points ?? []).some((point) =>
    isEmptyGroup(point.label),
  );
  const drawn = useMemo(
    () =>
      showsEmpty || !hasEmpty || !data
        ? data
        : {
            ...data,
            points: (data.points ?? []).filter(
              (point) => !isEmptyGroup(point.label),
            ),
          },
    [data, hasEmpty, showsEmpty],
  );
  const hasPoints = (drawn?.points?.length ?? 0) > 0;
  // A pie's parts are its points - one slice per category - so naming its
  // series names the measure over and over ("Amount") and says nothing about
  // which slice is which. Bars of one series over categories are the same
  // thing stood up: each bar is a kind of thing rather than a step along a
  // scale, so each gets its own colour and its own name. Bars over time are
  // one thing measured again and again, which is one colour and one name.
  const barsAreParts = isBarsOfParts(type, drawn);
  // The empty group keeps its place in the legend whether or not it is drawn:
  // it is what the eye beside it is about, and a name that disappears when it
  // is hidden cannot be brought back.
  const legendData = loading
    ? ["xxxxxxx", "xxxxxxxxxxxxxxxx"]
    : hasPoints
      ? (type === "pie" || barsAreParts
          ? data?.points?.map((point) => point.label)
          : drawn?.series) || []
      : [];

  // What putting the empty group away would take out of the chart, in the
  // words the chart already uses: the rows it counts, and the thing it is cut
  // by. Either can be missing - a chart drawn outside a widget knows no rows,
  // and a chart with no axis name has nothing to call the grouping - so each
  // falls back to the plainest word for itself rather than to nothing.
  const emptySaid = `${(rows ?? "rows").toLowerCase()} with no ${
    (data?.labelAxis ?? "").trim().toLowerCase() || "group"
  }`;

  const chartRef = useRef(null);
  const isInView = useInView(chartRef, { once: true, amount: 0.5 });
  const COLOR_ORDER = useMemo(() => {
    // A part keeps its colour whether or not its neighbours are drawn: the
    // colour follows the name, taken in the order the names first came, and
    // the empty group takes none of them - it is drawn as shading, and a
    // colour spent on it would shift every colour after it the moment it was
    // put away.
    if (barsAreParts || type === "pie") {
      const order = type === "pie" ? PIE_COLOR_ORDER : BAR_COLOR_ORDER;
      let taken = 0;
      return (data?.points ?? []).map((point) =>
        isEmptyGroup(point.label)
          ? EMPTY_HATCH_CSS
          : order[taken++ % order.length],
      );
    }
    if (type === "bar" && lineKeys?.length) {
      const series = data?.series || [];
      const barColorIdx = { current: 0 };
      const lineColorIdx = { current: 0 };
      return series.map((s) => {
        if (lineKeys.includes(s)) {
          return LINE_COLOR_ORDER[
            lineColorIdx.current++ % LINE_COLOR_ORDER.length
          ];
        }
        return BAR_COLOR_ORDER[barColorIdx.current++ % BAR_COLOR_ORDER.length];
      });
    }
    if (type === "bar") return BAR_COLOR_ORDER;
    return LINE_COLOR_ORDER;
  }, [type, lineKeys, data?.series, data?.points, barsAreParts]);

  // What each name is drawn in, by name rather than by where it ended up:
  // hiding one part must not repaint the others.
  const colourOfLabel = useMemo(() => {
    const byLabel = new Map<string, string>();
    (data?.points ?? []).forEach((point, index) => {
      byLabel.set(point.label, COLOR_ORDER[index % COLOR_ORDER.length]);
    });
    return (label: string) => byLabel.get(label) ?? COLOR_ORDER[0];
  }, [data?.points, COLOR_ORDER]);

  loading = loading || !isInView;

  // Positioned either way: the plot inside it is laid over its own box, and
  // without somewhere to hang it lands over the whole card - which is how the
  // legend came to sit on top of the topmost axis label.
  // What the pie keeps clear on every side, and how much of it is hole: the
  // placeholder is drawn to the same measure so nothing moves when it lands.
  const pieSize = PIE_SIZE_CONFIGS[size];
  const pieInset = 2 * (pieSize.margin.top + pieSize.activeOuterRadiusOffset);
  const pieHole = Math.round(pieSize.innerRadius * 100);

  return (
    <div
      ref={chartRef}
      // As tall as whatever is holding it, by being stretched to it and
      // not by asking for all of its height: a percentage is only worth
      // something against a height that was set, and a body that is
      // merely *at least* so tall has not set one. Asked for 100% of
      // that, the chart came out as tall as its legend and drew nothing.
      className={cn(
        "flex w-full min-w-0",
        fill ? "flex-1 min-h-0 self-stretch" : "h-(--height-2)",
        "relative flex-col",
        fill ? "justify-start" : "justify-between",
      )}
      style={
        {
          "--height-2": !fill
            ? Number(chartHeight + "px") <= 1
              ? `${Number(chartHeight + "px") * 100}%`
              : chartHeight + "px"
            : undefined,
        } as CSSProperties
      }
    >
      <div
        className={cn(
          "flex flex-col w-full gap-2",
          hideTitle ? "h-auto" : "h-18",
          hideTitle && "min-h-0",
        )}
      >
        {!hideTitle && (
          <Typography level="title-md" className="text-muted-foreground">
            <Skeleton loading={loading}>
              {data?.title || "ChartTitleLong"}
            </Skeleton>
          </Typography>
        )}
        <div className="flex items-center justify-between gap-2 min-w-0">
          <ChartLegend
            labels={legendData}
            colors={COLOR_ORDER}
            loading={loading}
            empty={
              hasEmpty
                ? {
                    isShown: showsEmpty,
                    onToggle: () => setShowsEmpty((shown) => !shown),
                    said: emptySaid,
                  }
                : undefined
            }
          />
        </div>
      </div>
      {!loading && drawn != null && hasPoints && (
        <div
          className={cn(
            fill
              ? "flex-1 min-h-0 w-full relative"
              : "h-(--height-2) flex-1 min-h-0 w-full relative",
          )}
          style={
            {
              "--height-2": !fill
                ? Number(plotHeight + "px") <= 1
                  ? `${Number(plotHeight + "px") * 100}%`
                  : plotHeight + "px"
                : undefined,
            } as CSSProperties
          }
        >
          {type === "bar" && (
            <BarChart
              data={drawn}
              size={size}
              lineKeys={lineKeys}
              colourOf={barsAreParts ? colourOfLabel : undefined}
            />
          )}
          {type === "pie" && (
            <ChartPie data={drawn} size={size} colourOf={colourOfLabel} />
          )}
          {type === "line" && <LineChart data={drawn} size={size} />}
        </div>
      )}
      {!loading && (drawn == null || !hasPoints) && (
        <div
          className={cn(
            "flex flex-col items-center justify-center gap-2",
            fill
              ? "flex-1 min-h-0 w-full relative"
              : "h-(--height-2) flex-1 min-h-0 w-full relative",
          )}
          style={
            {
              "--height-2": !fill
                ? Number(plotHeight + "px") <= 1
                  ? `${Number(plotHeight + "px") * 100}%`
                  : plotHeight + "px"
                : undefined,
            } as CSSProperties
          }
        >
          <Typography level="body-sm" className="text-muted-foreground/80">
            No data for this period
          </Typography>
        </div>
      )}
      {loading && (
        <div
          // What the pie's placeholder measures itself against.
          className={cn(
            "flex flex-row gap-2 w-full justify-center items-center @container-[size]",
            fill
              ? "flex-1 min-h-0 w-full relative"
              : "h-(--height-2) flex-1 min-h-0 w-full relative",
          )}
          style={
            {
              "--height-2": !fill
                ? Number(plotHeight + "px") <= 1
                  ? `${Number(plotHeight + "px") * 100}%`
                  : plotHeight + "px"
                : undefined,
            } as CSSProperties
          }
        >
          {["bar", "line"].includes(type) && (
            <Skeleton variant="inline" width="100%" height="100%" />
          )}
          {type === "pie" && (
            // The pie that will be here, in the room it will have: as wide as
            // the smaller side of the plot, less the margin the pie keeps for
            // the slice that lifts on hover, and a ring because the pie is.
            //
            // Measured in container units rather than percentages. A chart on
            // a report fills its card, so the plot's height is whatever the
            // card's flex leaves it - and a percentage of a height like that
            // comes to nothing. Asked to be as tall as the plot and as wide as
            // it was tall, the circle took its size from its width instead:
            // on a card six hundred wide, a disc six hundred across.
            <Skeleton
              variant="circular"
              className="rounded-[50%] shrink-0 w-(--width-2) h-(--width-2) mask-(--mask-image) [-webkit-mask-image:var(--mask-image)]"
              style={
                {
                  "--width-2":
                    Number(`calc(100cqmin - ${pieInset}px)`) <= 1
                      ? `${Number(`calc(100cqmin - ${pieInset}px)`) * 100}%`
                      : `calc(100cqmin - ${pieInset}px)`,
                  "--mask-image": `radial-gradient(closest-side, transparent ${pieHole}%, #000 ${pieHole}%)`,
                } as CSSProperties
              }
            />
          )}
        </div>
      )}
    </div>
  );
}

export const Chart = memo(ChartComponent);
